import { BoxShopSalesPayement } from "@/modules/boxShop/form/boxShopFormik"
import { CouponType } from "@/modules/common/couponObject/gateway/couponObjectApiDto"
import { word } from "@/modules/core/utils/i18n"
import { OutOfBandTransactionStatus } from "@/modules/finances/boxPaymentAtCounter/entities/outOfBandTransactionType"
import { z } from "zod"

/**
 * Validation schema for the box shop sales (new order) form.
 *
 * It is a factory because the maximum value of a DIRECT coupon depends on the
 * current order subtotal (sum of price * quantity for every order item).
 */
export function boxShopSalesFormSchema(subtotal: number) {
  return z
    .object({
      payment: z.enum(BoxShopSalesPayement, { message: word("form.error.payment.required") }),
      outOfBand: z.enum(OutOfBandTransactionStatus).nullable(),
      items: z.array(
        z.object({
          id: z.string().min(1, word("form.error.item.id.required")),
          quantity: z.number().min(1, word("form.error.item.quantity.min", { count: 1 })),
        }),
      ),
      coupon: z
        .object({
          type: z.enum(CouponType),
          value: z.number(),
        })
        .nullable(),
      userId: z.string().nullable(),
    })
    .superRefine((data, ctx) => {
      // A payment method (outOfBand) is required when the payment is made directly.
      if (data.payment === BoxShopSalesPayement.DIRECT && !data.outOfBand) {
        ctx.addIssue({
          code: "custom",
          message: word("form.error.outOfBand.required"),
          path: ["outOfBand"],
        })
      }

      // The coupon value bounds depend on the coupon type.
      const coupon = data.coupon
      if (coupon) {
        if (Number.isNaN(coupon.value)) {
          ctx.addIssue({ code: "custom", message: word("form.error.coupon.value.typeError"), path: ["coupon"] })
        } else if (coupon.type === CouponType.PERCENT) {
          if (coupon.value < 0) {
            ctx.addIssue({
              code: "custom",
              message: word("form.error.coupon.percent.value.min", { count: 1 }),
              path: ["coupon"],
            })
          } else if (coupon.value > 100) {
            ctx.addIssue({
              code: "custom",
              message: word("form.error.coupon.percent.value.max", { count: 100 }),
              path: ["coupon"],
            })
          }
        } else {
          if (coupon.value < 0) {
            ctx.addIssue({
              code: "custom",
              message: word("form.error.coupon.direct.value.min", { count: 0 }),
              path: ["coupon"],
            })
          } else if (coupon.value > subtotal) {
            ctx.addIssue({
              code: "custom",
              message: word("form.error.coupon.direct.value.max", { count: subtotal }),
              path: ["coupon"],
            })
          }
        }
      }
    })
}
